import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { peso, clock } from "../lib/format";
import { playChime } from "../lib/chime";
import { Btn, Money, cn } from "../components/ui/kit";

const STATUS_STYLE = {
  awaiting_confirmation: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  confirmed: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  ready: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  served: "bg-zinc-500/20 text-zinc-300 border-zinc-600",
};

export default function WaiterApp() {
  const { data: orders, refetch } = usePoll(() => api.get("/orders").then((r) => r.data), 3000);
  const { data: menu } = usePoll(() => api.get("/menu").then((r) => r.data), 0);
  const { data: reqs, refetch: refetchReq } = usePoll(() => api.get("/service-requests").then((r) => r.data), 3000);
  const [selId, setSelId] = useState(null);
  const [billing, setBilling] = useState(false);
  const [discount, setDiscount] = useState(false);

  const list = (orders || []).filter((o) => o.status !== "served" || o.payment_status !== "paid");
  const sel = (orders || []).find((o) => o.id === selId);

  const confirm = async (o) => {
    try {
      const { data } = await api.post(`/orders/${o.id}/confirm`, {});
      if (data.chime) playChime();
      toast.success(`Order ${o.order_number} confirmed → kitchen notified`);
      refetch();
    } catch (e) { toast.error(apiError(e)); }
  };
  const served = async (o) => { try { await api.post(`/orders/${o.id}/served`); toast.success("Marked served"); refetch(); } catch (e) { toast.error(apiError(e)); } };
  const resolveReq = async (r) => { await api.post(`/service-requests/${r.id}/resolve`); refetchReq(); };

  return (
    <div className="min-h-[calc(100vh-49px)] bg-zinc-950 text-zinc-100">
      <div className="mx-auto grid max-w-[1600px] gap-4 p-4 lg:grid-cols-[1.2fr_1fr]">
        {/* Orders */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <h1 className="font-head text-2xl font-black">Service Floor</h1>
            {reqs?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {reqs.map((r) => (
                  <button key={r.id} data-testid={`req-${r.id}`} onClick={() => resolveReq(r)}
                    className="rounded-full border border-amber-500/40 bg-amber-500/15 px-3 py-1 text-xs font-bold text-amber-300 rc-pulse">
                    {r.table || "—"} · {r.type} ✕
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {list.length === 0 && <p className="text-sm text-zinc-500">No active orders. Place one from the Customer role.</p>}
            {list.map((o) => (
              <button key={o.id} data-testid={`order-card-${o.id}`} onClick={() => setSelId(o.id)}
                className={cn("rounded-2xl border p-4 text-left transition", selId === o.id ? "border-emerald-500 bg-emerald-500/5" : "border-zinc-800 bg-zinc-900 hover:border-zinc-700")}>
                <div className="flex items-center justify-between">
                  <span className="font-head text-lg font-black">{o.channel === "DINE-IN" ? o.table : o.channel}</span>
                  <span className="font-mono text-xs text-zinc-500">{o.order_number}</span>
                </div>
                <div className="mt-1 text-xs text-zinc-400">{o.items.reduce((s, i) => s + i.qty, 0)} items · {clock(o.created_at)}</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase", STATUS_STYLE[o.status])}>{o.status.replace(/_/g, " ")}</span>
                  <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold uppercase", o.payment_status === "paid" ? "bg-emerald-600 text-white" : o.payment_status === "partial" ? "bg-amber-500 text-zinc-950" : "bg-rose-600 text-white")}>{o.payment_status}</span>
                  {o.pending_sync && <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-[10px] font-bold text-rose-300">PENDING SYNC</span>}
                  <Money value={o.bill?.total} className="ml-auto text-sm font-bold" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:sticky lg:top-16 lg:h-fit">
          {!sel ? (
            <div className="grid h-64 place-items-center rounded-2xl border border-dashed border-zinc-800 text-sm text-zinc-600">Select an order</div>
          ) : (
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4 rc-pop" data-testid="order-detail">
              <div className="flex items-center justify-between">
                <h2 className="font-head text-xl font-black">{sel.channel === "DINE-IN" ? `Table ${sel.table}` : sel.channel}</h2>
                <span className="font-mono text-sm text-zinc-500">{sel.order_number}</span>
              </div>
              <div className="mt-3 space-y-2">
                {sel.items.map((it, i) => (
                  <div key={i} className="rounded-xl border border-zinc-800 p-2.5">
                    <div className="flex justify-between text-sm">
                      <span className="font-bold">{it.qty}× {it.name}</span>
                      <Money value={it.unit_price * it.qty} />
                    </div>
                    {it.modifiers?.length > 0 && <div className="text-xs text-emerald-400">{it.modifiers.map((m) => m.name).join(", ")}</div>}
                    {it.special_instructions && <div className="text-xs italic text-zinc-400">“{it.special_instructions}”</div>}
                  </div>
                ))}
              </div>

              <BillLines bill={sel.bill} balance={sel.balance} paid={sel.paid_amount} />

              <div className="mt-4 grid grid-cols-2 gap-2">
                {sel.status === "awaiting_confirmation" && (
                  <Btn variant="emerald" className="col-span-2 py-3" data-testid="confirm-order-btn" onClick={() => confirm(sel)}>✓ Confirm Order</Btn>
                )}
                {["confirmed", "ready"].includes(sel.status) && (
                  <Btn variant="indigo" data-testid="served-btn" onClick={() => served(sel)}>Mark Served</Btn>
                )}
                <Btn variant="outline" className="text-zinc-200" data-testid="discount-btn" onClick={() => setDiscount(true)}>Discount</Btn>
                <Btn variant="amber" data-testid="billing-btn" onClick={() => setBilling(true)}>Bill & Pay</Btn>
              </div>
            </div>
          )}
        </div>
      </div>

      {billing && sel && <BillingModal order={sel} onClose={() => setBilling(false)} onRefresh={refetch} />}
      {discount && sel && <DiscountModal order={sel} onClose={() => setDiscount(false)} onDone={() => { setDiscount(false); refetch(); }} />}
    </div>
  );
}

function BillLines({ bill, balance, paid }) {
  if (!bill) return null;
  return (
    <div className="mt-3 space-y-1 rounded-xl bg-zinc-950/60 p-3 text-sm">
      <Row l="Subtotal" v={bill.subtotal} />
      {bill.vat_exemption > 0 && <Row l="VAT Exemption (Senior/PWD)" v={-bill.vat_exemption} className="text-emerald-400" />}
      {bill.discount_amount > 0 && <Row l="Discount" v={-bill.discount_amount} className="text-emerald-400" />}
      {bill.service_charge > 0 && <Row l="Service Charge" v={bill.service_charge} />}
      {bill.tip > 0 && <Row l="Tip" v={bill.tip} className="text-amber-300" />}
      <div className="my-1 h-px bg-zinc-800" />
      <Row l="Total" v={bill.total} bold />
      <Row l="Paid" v={paid} className="text-emerald-400" />
      <Row l="Balance" v={balance} bold className={balance > 0 ? "text-rose-400" : "text-emerald-400"} />
    </div>
  );
}
function Row({ l, v, bold, className = "" }) {
  return (
    <div className={cn("flex justify-between", bold && "font-bold", className)}>
      <span>{l}</span><Money value={v} />
    </div>
  );
}

function DiscountModal({ order, onClose, onDone }) {
  const [type, setType] = useState("senior");
  const [value, setValue] = useState(0);
  const [name, setName] = useState("");
  const [idNo, setIdNo] = useState("");
  const apply = async () => {
    try {
      await api.post(`/orders/${order.id}/discount`, { type, value: Number(value), customer_name: name, id_type: type.toUpperCase(), id_number: idNo });
      toast.success("Discount applied (logged to audit)");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal onClose={onClose} title="Apply Discount">
      <div className="grid grid-cols-2 gap-2">
        {["senior", "pwd", "percent", "amount", "none"].map((t) => (
          <button key={t} onClick={() => setType(t)} data-testid={`disc-${t}`}
            className={cn("rounded-xl border px-3 py-2 text-sm font-bold uppercase", type === t ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-zinc-700 text-zinc-300")}>{t}</button>
        ))}
      </div>
      {["senior", "pwd"].includes(type) && (
        <>
          <input placeholder="Customer name" value={name} onChange={(e) => setName(e.target.value)} className="mt-3 w-full rounded-xl border border-zinc-700 bg-zinc-800 px-3 py-2 outline-none" data-testid="disc-name" />
          <input placeholder={`${type.toUpperCase()} ID number`} value={idNo} onChange={(e) => setIdNo(e.target.value)} className="mt-2 w-full rounded-xl border border-zinc-700 bg-zinc-800 px-3 py-2 outline-none" data-testid="disc-id" />
          <p className="mt-2 text-xs text-zinc-500">Applies 20% discount + 12% VAT exemption (configurable in Super Admin).</p>
        </>
      )}
      {["percent", "amount"].includes(type) && (
        <input type="number" placeholder={type === "percent" ? "% off" : "₱ amount"} value={value} onChange={(e) => setValue(e.target.value)} className="mt-3 w-full rounded-xl border border-zinc-700 bg-zinc-800 px-3 py-2 outline-none" data-testid="disc-value" />
      )}
      <Btn variant="emerald" className="mt-4 w-full py-3" onClick={apply} data-testid="apply-discount-btn">Apply Discount</Btn>
    </Modal>
  );
}

function BillingModal({ order, onClose, onRefresh }) {
  const { data: corps } = usePoll(() => api.get("/corporate-accounts").then((r) => r.data), 0);
  const [ord, setOrd] = useState(order);
  const [tab, setTab] = useState("full");
  const [method, setMethod] = useState("Cash");
  const [amount, setAmount] = useState(order.balance);
  const [corpId, setCorpId] = useState("");
  const methods = ["Cash", "GCash", "Maya", "Credit Card", "Debit Card", "Bank / Online", "Corporate Account"];

  const refresh = async () => {
    const { data } = await api.get(`/orders/${order.id}`);
    setOrd(data);
    setAmount(data.balance);
    onRefresh && onRefresh();
  };

  const setTipPct = async (pct) => {
    const amt = Math.round((ord.bill.subtotal * pct) / 100 * 100) / 100;
    await api.post(`/orders/${order.id}/tip`, { amount: amt });
    toast.success(pct === 0 ? "No tip" : `${pct}% tip added`);
    await refresh();
  };

  const pay = async () => {
    try {
      const payAmt = tab === "full" ? ord.balance : Number(amount);
      await api.post(`/orders/${order.id}/pay`, {
        amount: payAmt, method: method === "Corporate Account" ? "Corporate Account" : method,
        type: tab, corporate_account_id: method === "Corporate Account" ? corpId : null,
        split_label: tab === "split" ? "Guest split" : null,
      });
      toast.success(`${method} payment of ${peso(payAmt)} recorded`);
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal onClose={onClose} title={`Bill · ${ord.order_number}`}>
      <BillLines bill={ord.bill} balance={ord.balance} paid={ord.paid_amount} />

      <div className="mt-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Gratuity — optional & pooled for the team</div>
      <div className="mt-1 flex gap-2">
        {[0, 5, 10, 15].map((p) => (
          <button key={p} onClick={() => setTipPct(p)} data-testid={`tip-${p}`}
            className="rounded-full border border-zinc-700 px-3 py-1.5 text-xs font-bold text-zinc-200 hover:border-amber-400 hover:text-amber-300">{p === 0 ? "No Tip" : `${p}%`}</button>
        ))}
      </div>

      <div className="mt-4 flex gap-2">
        {["full", "split", "partial", "corporate"].map((t) => (
          <button key={t} onClick={() => { setTab(t); if (t === "corporate") setMethod("Corporate Account"); }}
            data-testid={`paytab-${t}`}
            className={cn("flex-1 rounded-xl border px-2 py-2 text-xs font-bold uppercase", tab === t ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-zinc-700 text-zinc-300")}>{t}</button>
        ))}
      </div>

      {tab !== "corporate" && (
        <div className="mt-3 flex flex-wrap gap-2">
          {methods.filter((m) => m !== "Corporate Account").map((m) => (
            <button key={m} onClick={() => setMethod(m)} data-testid={`method-${m.replace(/[\s/]+/g, "-").toLowerCase()}`}
              className={cn("rounded-full border px-3 py-1.5 text-xs font-semibold", method === m ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-zinc-700 text-zinc-300")}>{m}</button>
          ))}
        </div>
      )}
      {tab === "corporate" && (
        <select value={corpId} onChange={(e) => setCorpId(e.target.value)} className="mt-3 w-full rounded-xl border border-zinc-700 bg-zinc-800 px-3 py-2 outline-none" data-testid="corp-select">
          <option value="">Select corporate account…</option>
          {(corps || []).map((c) => <option key={c.id} value={c.id}>{c.company_name} · limit {peso(c.authorized_limit)}</option>)}
        </select>
      )}

      {(tab === "split" || tab === "partial" || tab === "corporate") && (
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount"
          className="mt-3 w-full rounded-xl border border-zinc-700 bg-zinc-800 px-3 py-2 font-mono outline-none" data-testid="pay-amount" />
      )}

      <Btn variant="emerald" className="mt-4 w-full py-3" onClick={pay} data-testid="record-payment-btn" disabled={ord.balance <= 0}>
        {ord.balance <= 0 ? "Fully Paid ✓" : `Record ${peso(tab === "full" ? ord.balance : Number(amount) || 0)}`}
      </Btn>
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-md overflow-auto rounded-t-3xl border border-zinc-800 bg-zinc-900 p-5 text-zinc-100 sm:rounded-3xl rc-rise" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-head text-lg font-black">{title}</h3>
          <button onClick={onClose} className="text-zinc-400">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
